import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip, TooltipProps } from 'recharts';

import { ReactComponent as PinActiveIcon } from '../../../../assets/icons/pinActiveIcon.svg';
import { ReactComponent as PinIcon } from '../../../../assets/icons/PinIcon.svg';
import currentTimeZone from '../../../../config/timeZone';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { setNotificationMessage } from '../../../../redux/slices/NotificationManagementSlice';
import { clearActivePin, setActivePin } from '../../../../redux/slices/ReportSlice';
import BatteryLoader from '../../../../shared-components/battery-loader/BatteryLoader';
import NoDataAvailable from '../../../../shared-components/no-data-available/NoDataAvailable';
import FormatDuration from '../../../../shared-functions/FormatDuration';
import report from '../../../../utils/api/reports';

interface ChargerUptimeRow {
  chargingPointSn: string;
  uptimeHours: number;
  downtimeHours: number;
}

interface UptimeDowntimeSummary {
  totalUptimeHours: number;
  totalDowntimeHours: number;
  chargers: ChargerUptimeRow[];
}

interface PieDataItem {
  name: string;
  value: number;
  color: string;
  duration: string;
}

/** Color code for uptime representation in chart. */
const UPTIME_COLOR = '#2E9E3A';
/** Color code for downtime representation in chart. */
const DOWNTIME_COLOR = '#E41010';

/**
 * ChargerReportChart
 * Renders the account-wide uptime vs downtime donut chart for the currently
 * selected account and date range, sourced from the TimescaleDB-backed
 * uptime-vs-downtime-report endpoint.
 * @returns {React.ReactElement} The rendered chart component.
 */
function ChargerReportChart() {
  const { t } = useTranslation('report');
  const dispatch = useAppDispatch();
  const reportsData = useAppSelector((state) => state.reportData);
  const { accountId, reportFilter } = reportsData;
  const { startDate, endDate } = reportFilter;

  const [summary, setSummary] = useState<UptimeDowntimeSummary>({
    totalUptimeHours: 0,
    totalDowntimeHours: 0,
    chargers: [],
  });
  const [loading, setLoading] = useState<boolean>(false);

  /**
   * Fetches total uptime vs downtime hours for the selected account and date range.
   * @returns {Promise<void>} A promise that resolves when the data fetch completes.
   */
  const fetchUptimeVsDowntimeReport = async () => {
    if (!accountId || !startDate || !endDate) {
      return;
    }

    setLoading(true);
    try {
      const response = await report.fetchChargerUptimeVsDowntimeReport({
        accountCustomId: accountId,
        startDate,
        endDate,
        timeZone: currentTimeZone,
      });

      if (response.status === 200) {
        setSummary({
          totalUptimeHours: response.data?.totalUptimeHours || 0,
          totalDowntimeHours: response.data?.totalDowntimeHours || 0,
          chargers: response.data?.chargers || [],
        });
      }
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          type: 'error',
          notificationDetails: {
            statusCode: error?.response?.data?.code || 200,
            message: error?.response?.data?.message || 'Something went wrong!',
          },
        }),
      );
    } finally {
      setLoading(false);
    }
  };

  /**
   * Custom tooltip component for the uptime vs downtime pie chart.
   * @param {TooltipProps<number, string>} properties - The recharts tooltip properties.
   * @returns {React.ReactElement | null} The rendered tooltip element, or null if inactive.
   */
  function CustomTooltip(properties: TooltipProps<number, string>) {
    const { active, payload } = properties;
    if (active && payload && payload.length > 0) {
      const data = payload[0].payload;
      return (
        <div className="connector-status-custom-tooltip">
          <p
            className="label"
            style={{
              color: data?.payload?.color,
            }}>{`${data.name} : ${data?.duration || FormatDuration(data?.value, { formatBy: 'hr', showDays: true, defaultValue: '00d 00h 00m 00s' })}`}</p>
        </div>
      );
    }
    return null;
  }

  /**
   * Handles pin click to toggle active state for a given report name.
   * @param {string} name - The name of the report pin to toggle.
   * @returns {void}
   */
  const handleClick = (name: string) => {
    if (reportsData.activePinName.includes(name)) {
      dispatch(clearActivePin(name));
    } else {
      dispatch(setActivePin(name));
    }
  };

  useEffect(() => {
    fetchUptimeVsDowntimeReport();
  }, [accountId, startDate, endDate]);

  const totalHours = summary.totalUptimeHours + summary.totalDowntimeHours;
  const isEmpty = totalHours <= 0;

  const pieData: PieDataItem[] = [
    {
      name: t('uptime'),
      value: summary.totalUptimeHours,
      color: UPTIME_COLOR,
      duration: FormatDuration(summary.totalUptimeHours, {
        formatBy: 'hr',
        showDays: true,
        defaultValue: '00d 00h 00m 00s',
      }),
    },
    {
      name: t('downtime'),
      value: summary.totalDowntimeHours,
      color: DOWNTIME_COLOR,
      duration: FormatDuration(summary.totalDowntimeHours, {
        formatBy: 'hr',
        showDays: true,
        defaultValue: '00d 00h 00m 00s',
      }),
    },
  ];

  /**
   * Renders the chart body based on loading/empty/loaded state.
   * @returns {React.ReactElement} The rendered chart content.
   */
  const renderChartContent = (): React.ReactElement => {
    if (loading) {
      return (
        <center className="loader-button">
          <BatteryLoader />
        </center>
      );
    }

    if (isEmpty) {
      return <NoDataAvailable />;
    }

    return (
      <>
        <div className="piechart-container">
          <ResponsiveContainer width="100%" height={240}>
            <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
              <Pie
                data={pieData}
                cx="50%"
                cy="50%"
                innerRadius={60}
                outerRadius={100}
                paddingAngle={2}
                dataKey="value"
                isAnimationActive={false} // Better for touch devices
              >
                {pieData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip />}
                wrapperStyle={{ zIndex: 1000 }} // Ensure tooltip appears above other elements
              />
            </PieChart>
          </ResponsiveContainer>

          <div className="total-sessions-wrap">
            <p>{t('totalHours')}</p>
            <span>{totalHours.toFixed(1)}hrs</span>
          </div>
        </div>
        <div className="PieChart-heading">
          <div className="pie-chart-label vertical-legend">
            {pieData.map((eachData) => (
              <div key={eachData.name} className="each-label">
                <div
                  className="each-label-color"
                  style={{ '--label-color': eachData.color } as React.CSSProperties}
                />
                <p>{eachData.name}</p>
                <div className="each-label-value">
                  <span>{eachData.duration}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </>
    );
  };

  /**
   * Renders the per-charger uptime vs downtime breakdown list below the pie chart.
   * @returns {React.ReactElement | null} The rendered list, or null when there's nothing to show.
   */
  const renderChargerList = (): React.ReactElement | null => {
    if (loading || isEmpty || summary.chargers.length === 0) {
      return null;
    }

    return (
      <div className="charger-uptime-list">
        {summary.chargers.map((charger) => {
          const rowTotal = charger.uptimeHours + charger.downtimeHours;
          const uptimePercent = rowTotal > 0 ? (charger.uptimeHours / rowTotal) * 100 : 0;

          return (
            <div key={charger.chargingPointSn} className="charger-uptime-list-item">
              <div className="charger-row-top">
                <p className="charger-sn" title={charger.chargingPointSn}>
                  {charger.chargingPointSn}
                </p>
                <div className="charger-row-values">
                  <span className="value uptime-value">{charger.uptimeHours.toFixed(1)}hrs</span>
                  <span className="value downtime-value">{charger.downtimeHours.toFixed(1)}hrs</span>
                </div>
              </div>
              <div className="charger-row-bar">
                <div className="bar-segment uptime" style={{ width: `${uptimePercent}%` }} />
                <div className="bar-segment downtime" style={{ width: `${100 - uptimePercent}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="energy-report-chart-wrap charger-uptime-report">
      <div className="energy-report-container">
        <div className="report-header">
          <div className="report-title">
            <button
              type="button"
              aria-label="Pin uptime vs downtime report"
              className={`pin-button ${
                reportsData.activePinName.includes('uptimeVsDowntime') ? 'active' : ''
              }`}
              onClick={() => handleClick('uptimeVsDowntime')}>
              {reportsData.activePinName.includes('uptimeVsDowntime') ? (
                <span className="pin-icon">
                  <PinActiveIcon />
                </span>
              ) : (
                <span className="pin-icon-wrapper">
                  <span className="pin-icon default">
                    <PinIcon />
                  </span>
                </span>
              )}
            </button>
            {t('uptime')} {t('vs')} {t('downtime')}
          </div>
        </div>

        <div className="energy-report-container chart-content-container">{renderChartContent()}</div>
        {renderChargerList()}
      </div>
    </div>
  );
}

export default ChargerReportChart;
